var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import Config from './Config';
import { resolveCurrentItemId } from './product';
// Topics stays on when core-api is too old to say otherwise (the behaviour
// before the setting existed), or when there is no core-api to ask.
var DEFAULT_TRACKING_SETTINGS = { browsingTopics: true };
function stripTrailingSlash(url) {
    return url.length > 1 && url.endsWith('/') ? url.slice(0, -1) : url;
}
/**
 * On-site personalization.
 *
 * Fetches the components the current visitor qualifies for from core-api
 * (/personalize/serve — qualification, incl. the optional segment gate, is done
 * server-side) and injects each one above / below / in place of the element
 * resolved by its xpath, on pages whose url matches the component's rules.
 *
 * Runs on init and re-applies on SPA navigation (history API + popstate). A
 * short-lived MutationObserver covers targets that render after the initial
 * pass. The server caches per-visitor responses, so re-fetching on navigation
 * stays cheap and keeps personalization practically real-time per page view.
 */
var Personalize = /** @class */ (function () {
    function Personalize(client, instance, device, emit, formSubmit) {
        var _this = this;
        // Nodes we injected, so a re-render can remove its own previous output before
        // re-applying (avoids duplicates as the SPA DOM persists across navigations).
        this.injected = [];
        // `replace` rules detach the page's original target and put our node in its
        // place. We keep each (node -> original) pair so cleanup can restore the
        // original before re-rendering: otherwise the target's xpath stops resolving
        // after the first apply (the original is gone), and any re-render — the
        // MutationObserver reacting to our own write, or an SPA re-render — drops the
        // component for good. Restoring keeps `replace` idempotent across renders.
        this.replaced = [];
        this.components = [];
        this.started = false;
        // Increments per fetch, so a slow response for a page the visitor already
        // left can't overwrite the components of the page they are on.
        this.fetchSeq = 0;
        // Impressions already counted, keyed by `${componentId}@${url}`, so the
        // MutationObserver re-render churn (and SPA re-renders of the same page) emit
        // at most one impression per component per page. A new url re-arms counting.
        this.impressed = new Set();
        // componentId -> recommendationId, so an impression can be attributed to the
        // recommendation backing the component (clicks are attributed per-anchor).
        this.componentReco = new Map();
        // componentId -> variantId, so impression / click / conversion events can be
        // attributed to the A/B variant the server assigned this visitor.
        this.componentVariant = new Map();
        // componentId -> formIds, so an impression can be attributed to the Form
        // Builder form(s) embedded in the component's markup (same purpose as
        // componentReco, but a component's html may embed more than one form).
        this.componentForm = new Map();
        // componentId -> cooldownSeconds, so an impression knows how long to
        // suppress the component for afterwards (see LS_PERSONALIZE_LAST_SHOWN).
        this.componentCooldown = new Map();
        this.client = client;
        this.instance = instance;
        this.device = device;
        this.emit = emit;
        this.formSubmit = formSubmit;
        // Runtime override (window.Tracker.apiUrl) wins over the build-time default.
        this.apiUrl = (window.Tracker && window.Tracker.apiUrl) || Config.API_URL;
        this.trackingSettingsPromise = new Promise(function (resolve) {
            _this.resolveTrackingSettings = resolve;
        });
    }
    /**
     * The instance's tracker settings, from the first /personalize/serve
     * response of this page. A failed first request resolves to everything
     * off for this page: an opt-out we couldn't read must not be ignored.
     */
    Personalize.prototype.trackingSettings = function () {
        return this.trackingSettingsPromise;
    };
    /** Begin: fetch components, render, and wire navigation/DOM listeners. */
    Personalize.prototype.start = function () {
        return __awaiter(this, void 0, Promise, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.started)
                            return [2 /*return*/];
                        this.started = true;
                        this.wireNavigation();
                        return [4 /*yield*/, this.refresh()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /** Re-fetch the qualifying components and re-render for the current page. */
    Personalize.prototype.refresh = function () {
        return __awaiter(this, void 0, Promise, function () {
            var seq, components, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.lastFetchKey = this.navigationKey();
                        seq = ++this.fetchSeq;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.fetchComponents()];
                    case 2:
                        components = _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        console.warn('Personalize: failed to fetch components', error_1);
                        this.resolveTrackingSettings({ browsingTopics: false });
                        components = [];
                        return [3 /*break*/, 4];
                    case 4:
                        if (seq !== this.fetchSeq)
                            return [2 /*return*/];
                        this.components = components;
                        this.render();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * The part of the address a new fetch depends on: path and query, minus the
     * params the tracker itself strips (bid, pid) and minus the fragment.
     */
    Personalize.prototype.navigationKey = function () {
        var params = new URLSearchParams(window.location.search);
        params.delete(Config.QUERY_PARAM_BROWSER_ID);
        params.delete(Config.QUERY_PARAM_PID);
        return "".concat(window.location.pathname, "?").concat(params.toString());
    };
    Personalize.prototype.fetchComponents = function () {
        return __awaiter(this, void 0, Promise, function () {
            var params, pid, did, res, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.apiUrl) {
                            this.resolveTrackingSettings(DEFAULT_TRACKING_SETTINGS);
                            return [2 /*return*/, []];
                        }
                        params = new URLSearchParams({
                            client: String(this.client),
                            instance: String(this.instance),
                            // Catalogue language for recommendation hydration.
                            lang: this.currentLang(),
                        });
                        pid = this.currentProductId();
                        if (pid)
                            params.set('pid', pid);
                        did = this.device.getDeviceID();
                        return [4 /*yield*/, fetch("".concat(this.apiUrl, "/personalize/serve?").concat(params.toString()), __assign({ method: 'GET', mode: 'cors', credentials: did ? 'include' : 'omit' }, (did ? { headers: { 'X-Device-Id': did } } : {})))];
                    case 1:
                        res = _a.sent();
                        if (!res.ok) {
                            this.resolveTrackingSettings({ browsingTopics: false });
                            return [2 /*return*/, []];
                        }
                        return [4 /*yield*/, res.json()];
                    case 2:
                        data = _a.sent();
                        this.resolveTrackingSettings(__assign(__assign({}, DEFAULT_TRACKING_SETTINGS), data.tracking));
                        return [2 /*return*/, data.components || []];
                }
            });
        });
    };
    /** Page language as a short catalogue code (e.g. "en-US" -> "en"). */
    Personalize.prototype.currentLang = function () {
        var raw = document.documentElement.getAttribute('lang') ||
            navigator.language ||
            'en';
        return raw.split('-')[0].toLowerCase();
    };
    /**
     * Current product id, used to seed item-to-item recommendations. Resolved
     * fresh from the page's meta/Open Graph tags (no persisted global), re-read on
     * every navigation since the personalizer re-runs on SPA route changes.
     */
    Personalize.prototype.currentProductId = function () {
        return resolveCurrentItemId();
    };
    /** Remove previously injected nodes and re-apply matching components. */
    Personalize.prototype.render = function () {
        this.cleanup();
        for (var _i = 0, _a = this.components; _i < _a.length; _i++) {
            var component = _a[_i];
            if (this.isInCooldown(component))
                continue;
            for (var _b = 0, _c = component.rules || []; _b < _c.length; _b++) {
                var rule = _c[_b];
                if (this.urlMatches(rule)) {
                    this.applyRule(component, rule);
                }
            }
        }
    };
    // ---- Cooldown / frequency capping ------------------------------------------
    /**
     * Read the last-shown timestamps map (componentId -> ms epoch). Resilient to
     * private-mode / corrupt storage (returns {}).
     */
    Personalize.prototype.readLastShown = function () {
        try {
            var raw = localStorage.getItem(Config.LS_PERSONALIZE_LAST_SHOWN);
            if (!raw)
                return {};
            return JSON.parse(raw);
        }
        catch (_a) {
            return {};
        }
    };
    /** Whether this component was shown recently enough to still be suppressed. */
    Personalize.prototype.isInCooldown = function (component) {
        if (!component.cooldownSeconds || component.cooldownSeconds <= 0) {
            return false;
        }
        var lastShown = this.readLastShown()[String(component.id)];
        if (lastShown == null)
            return false;
        return Date.now() - lastShown < component.cooldownSeconds * 1000;
    };
    /** Record that a component was just shown, arming its cooldown window. */
    Personalize.prototype.markShown = function (componentId) {
        try {
            var shown = this.readLastShown();
            shown[String(componentId)] = Date.now();
            localStorage.setItem(Config.LS_PERSONALIZE_LAST_SHOWN, JSON.stringify(shown));
        }
        catch (_a) {
            // localStorage unavailable: cooldown just won't be enforced.
        }
    };
    Personalize.prototype.cleanup = function () {
        var _a, _b, _c;
        // Restore originals we swapped out for `replace`, so their xpath resolves
        // again on the next render. Done before removing injected nodes: putting the
        // original back detaches our node, so the removal loop below simply skips it.
        for (var _i = 0, _d = this.replaced; _i < _d.length; _i++) {
            var _e = _d[_i], node = _e.node, original = _e.original;
            (_a = node.parentNode) === null || _a === void 0 ? void 0 : _a.replaceChild(original, node);
        }
        this.replaced = [];
        for (var _f = 0, _g = this.injected; _f < _g.length; _f++) {
            var node = _g[_f];
            (_b = node.parentNode) === null || _b === void 0 ? void 0 : _b.removeChild(node);
        }
        this.injected = [];
        // Stop observing the nodes we are about to drop; the next render re-arms a
        // fresh observer over the freshly injected ones. The `impressed` set is kept
        // so already-counted components are not re-counted on re-render.
        (_c = this.impressionObserver) === null || _c === void 0 ? void 0 : _c.disconnect();
        this.impressionObserver = undefined;
    };
    Personalize.prototype.urlMatches = function (rule) {
        var href = window.location.href;
        switch (rule.urlMatchType) {
            case 'exact':
                // A bare-origin href always carries a trailing slash (e.g.
                // "https://example.com/"), which a urlMatch saved without one would
                // never equal — silently stranding every homepage "exact" rule.
                // Ignore a single trailing slash on either side before comparing.
                return stripTrailingSlash(href) === stripTrailingSlash(rule.urlMatch);
            case 'prefix':
                return href.startsWith(rule.urlMatch);
            case 'regex':
                try {
                    return new RegExp(rule.urlMatch).test(href);
                }
                catch (_a) {
                    return false;
                }
            case 'contains':
            default:
                return href.includes(rule.urlMatch);
        }
    };
    Personalize.prototype.resolveTarget = function (xpath) {
        try {
            var result = document.evaluate(xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null);
            var node = result.singleNodeValue;
            return node instanceof Element ? node : null;
        }
        catch (_a) {
            return null;
        }
    };
    // Build the component node from its html, tagging it (and any css) so cleanup
    // can find it again. Styles are injected once per component.
    //
    // The actual content lives in a shadow root, not as light-DOM children of
    // the wrapper: this fully isolates each component's (and each embedded
    // form's) CSS from the rest of the page in both directions — the host
    // page's own generic selectors can't leak in, and this component's own
    // `css` can't leak out onto unrelated components/forms elsewhere on the
    // page (previously possible once FORM_CSS moved to `@layer`, since any
    // unlayered style anywhere on the page would then win over it). `--cdpf-*`
    // custom properties still reach inside: property inheritance is not
    // interrupted by a shadow boundary, only selector matching is — so a host
    // page can still theme a form via `[data-cdpf-personalize]{--cdpf-...}`.
    // `mode:'open'` (not `'closed'`) is required: `node.shadowRoot` is read
    // below by `observeImpression`/`wireClicks`.
    Personalize.prototype.buildNode = function (component) {
        var _a;
        var wrapper = document.createElement('div');
        wrapper.setAttribute(Config.PERSONALIZE_MARKER, String(component.id));
        // The wrapper only exists so a re-render can find and remove its own
        // injections; `display: contents` makes it generate no box, so it is
        // layout-transparent — the component's own root element flows into the page
        // exactly where the rule places it.
        wrapper.style.display = 'contents';
        var shadow = wrapper.attachShadow({ mode: 'open' });
        // `submit` never crosses a shadow boundary (see FormSubmit.ts) — wire it
        // directly onto this shadow root so a form injected here still gets
        // intercepted, instead of silently falling through to a native page
        // reload with the fields leaked as a GET query string.
        (_a = this.formSubmit) === null || _a === void 0 ? void 0 : _a.listenOn(shadow);
        if (component.css) {
            var style = document.createElement('style');
            style.textContent = component.css;
            shadow.appendChild(style);
        }
        shadow.innerHTML += component.html;
        return wrapper;
    };
    Personalize.prototype.applyRule = function (component, rule) {
        var target = this.resolveTarget(rule.xpath);
        if (!target || !target.parentNode)
            return;
        var node = this.buildNode(component);
        var parent = target.parentNode;
        switch (rule.position) {
            case 'above':
                parent.insertBefore(node, target);
                break;
            case 'below':
                parent.insertBefore(node, target.nextSibling);
                break;
            case 'replace':
                parent.replaceChild(node, target);
                // Remember the displaced original so cleanup can restore it, keeping the
                // xpath resolvable for the next render (see `replaced`).
                this.replaced.push({ node: node, original: target });
                break;
        }
        this.injected.push(node);
        this.instrument(component, node);
    };
    /**
     * Wire performance tracking onto a freshly injected component node:
     * an impression when it becomes visible, and a `click` event carrying the
     * personalization id (+ CTA url) when a link/button inside it is clicked.
     * No-op when no emit sink was provided.
     */
    Personalize.prototype.instrument = function (component, node) {
        if (!this.emit)
            return;
        if (component.recommendationId != null) {
            this.componentReco.set(component.id, component.recommendationId);
        }
        if (component.formIds != null && component.formIds.length > 0) {
            this.componentForm.set(component.id, component.formIds);
        }
        if (component.cooldownSeconds != null && component.cooldownSeconds > 0) {
            this.componentCooldown.set(component.id, component.cooldownSeconds);
        }
        if (component.variantId != null) {
            this.componentVariant.set(component.id, component.variantId);
            // Remember the exposure so a later purchase can be attributed to this
            // variant even after navigation / a new session.
            this.persistAssignment(component.id, component.variantId, component.recommendationId);
        }
        this.observeImpression(component, node);
        this.wireClicks(component, node);
    };
    Personalize.prototype.impressionKey = function (componentId) {
        return "".concat(componentId, "@").concat(window.location.href);
    };
    /**
     * Emit a single `personalizationview` impression the first time the component
     * is seen on the current page. Prefers an IntersectionObserver (only counts
     * when the component actually scrolls into view); when that API is missing,
     * counts on inject as a graceful fallback.
     */
    Personalize.prototype.observeImpression = function (component, node) {
        var _this = this;
        var _a;
        if (this.impressed.has(this.impressionKey(component.id)))
            return;
        if (typeof IntersectionObserver === 'undefined') {
            this.recordImpression(component.id);
            return;
        }
        if (!this.impressionObserver) {
            this.impressionObserver = new IntersectionObserver(function (entries) {
                var _a, _b;
                for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
                    var entry = entries_1[_i];
                    if (!entry.isIntersecting)
                        continue;
                    // `Element.closest()` never crosses a shadow boundary, and the
                    // observed element now lives inside the component's shadow root
                    // while `PERSONALIZE_MARKER` is on its (light-DOM) host — resolve
                    // the host explicitly via the shadow root first.
                    var root_1 = entry.target.getRootNode();
                    var host = root_1 instanceof ShadowRoot ? root_1.host : entry.target;
                    var marker = (_a = host
                        .closest("[".concat(Config.PERSONALIZE_MARKER, "]"))) === null || _a === void 0 ? void 0 : _a.getAttribute(Config.PERSONALIZE_MARKER);
                    var id = marker != null ? Number(marker) : NaN;
                    if (Number.isFinite(id))
                        _this.recordImpression(id);
                    (_b = _this.impressionObserver) === null || _b === void 0 ? void 0 : _b.unobserve(entry.target);
                }
            });
        }
        // The wrapper is `display: contents` (no box of its own) and its content
        // lives in its shadow root, not as light-DOM children — observe the real
        // element children there, the component's actual rendered roots, skipping
        // the injected <style>.
        var root = (_a = node.shadowRoot) !== null && _a !== void 0 ? _a : node;
        for (var _i = 0, _b = Array.from(root.children); _i < _b.length; _i++) {
            var child = _b[_i];
            if (child.tagName !== 'STYLE')
                this.impressionObserver.observe(child);
        }
    };
    Personalize.prototype.recordImpression = function (componentId) {
        var _a;
        var key = this.impressionKey(componentId);
        if (this.impressed.has(key))
            return;
        this.impressed.add(key);
        if (this.componentCooldown.has(componentId)) {
            this.markShown(componentId);
        }
        var data = { personalizationId: componentId };
        var recoId = this.componentReco.get(componentId);
        if (recoId != null)
            data.recommendationId = recoId;
        var variantId = this.componentVariant.get(componentId);
        if (variantId != null)
            data.variantId = variantId;
        var formIds = this.componentForm.get(componentId);
        if (formIds != null)
            data.formIds = formIds;
        (_a = this.emit) === null || _a === void 0 ? void 0 : _a.call(this, 'personalizationview', data);
    };
    /**
     * Remove a single component's node from the DOM (restoring the original
     * displaced element for a `replace` rule, mirroring what `cleanup()` does
     * for every injected node on a full re-render), and stop tracking it as
     * currently injected. Used when the visitor dismisses the component via its
     * close control.
     */
    Personalize.prototype.dismissComponent = function (node) {
        var _a, _b;
        var replacedIdx = this.replaced.findIndex(function (r) { return r.node === node; });
        if (replacedIdx !== -1) {
            var original = this.replaced[replacedIdx].original;
            (_a = node.parentNode) === null || _a === void 0 ? void 0 : _a.replaceChild(original, node);
            this.replaced.splice(replacedIdx, 1);
        }
        else {
            (_b = node.parentNode) === null || _b === void 0 ? void 0 : _b.removeChild(node);
        }
        var injectedIdx = this.injected.indexOf(node);
        if (injectedIdx !== -1)
            this.injected.splice(injectedIdx, 1);
    };
    /**
     * Delegate clicks on the injected node: a click on (or inside) an element
     * carrying Config.PERSONALIZE_CLOSE_MARKER closes the component (the AI/
     * human-authored markup can't rely on inline `onclick`, since the HTML is
     * injected via innerHTML and never executes scripts). Otherwise, when the
     * click lands on (or inside) a link/button, emit a `click` event tagged
     * with the personalization id and the CTA url (`target`). Falls back to the
     * component marker when the clickable has no resolvable href.
     */
    Personalize.prototype.wireClicks = function (component, node) {
        var _this = this;
        node.addEventListener('click', function (event) {
            var _a, _b;
            // `event.target` is retargeted to `node` itself for a listener attached
            // outside the shadow tree the click actually happened in (`node` is the
            // shadow host) — `composedPath()[0]` is the real innermost element the
            // visitor clicked, regardless of the shadow boundary.
            var origin = event.composedPath()[0];
            // `Node.contains()` does not cross a shadow boundary either: a light-DOM
            // host never "contains" its own shadow-tree descendants. Check against
            // the shadow root (where the actual content — and `origin` — live) with
            // a light-DOM fallback for components that (in tests, or if `node` ever
            // isn't a shadow host) render straight into the light DOM.
            var scope = (_a = node.shadowRoot) !== null && _a !== void 0 ? _a : node;
            var closeEl = origin === null || origin === void 0 ? void 0 : origin.closest("[".concat(Config.PERSONALIZE_CLOSE_MARKER, "]"));
            if (closeEl && scope.contains(closeEl)) {
                _this.dismissComponent(node);
                return;
            }
            var clickable = origin === null || origin === void 0 ? void 0 : origin.closest('a[href], area[href], button, [role="button"]');
            if (!clickable || !scope.contains(clickable))
                return;
            var href = clickable.getAttribute('href');
            var target = href
                ? clickable.href
                : "".concat(Config.PERSONALIZE_MARKER, "=").concat(component.id);
            var data = {
                personalizationId: component.id,
                target: target,
            };
            if (component.variantId != null)
                data.variantId = component.variantId;
            // Attribute the click to a recommendation only when the clicked CTA is a
            // recommended product/article link (server-tagged with data-cdpf-reco).
            var recoEl = origin === null || origin === void 0 ? void 0 : origin.closest("[".concat(Config.RECO_MARKER, "]"));
            if (recoEl && scope.contains(recoEl)) {
                var recoId = Number(recoEl.getAttribute(Config.RECO_MARKER));
                if (Number.isFinite(recoId))
                    data.recommendationId = recoId;
            }
            (_b = _this.emit) === null || _b === void 0 ? void 0 : _b.call(_this, 'click', data);
        });
    };
    // ---- A/B conversion attribution -------------------------------------------
    /**
     * Read the persisted experiment assignments, dropping any older than the
     * attribution window. Keyed by personalizationId ->
     * { variantId, recommendationId?, ts }. Resilient to private-mode / corrupt
     * storage (returns {}).
     */
    Personalize.prototype.readAssignments = function () {
        try {
            var raw = localStorage.getItem(Config.LS_EXPERIMENT_ASSIGNMENTS);
            if (!raw)
                return {};
            var parsed = JSON.parse(raw);
            var now = Date.now();
            var fresh = {};
            for (var _i = 0, _a = Object.entries(parsed); _i < _a.length; _i++) {
                var _b = _a[_i], pid = _b[0], a = _b[1];
                if (a &&
                    typeof a.variantId === 'number' &&
                    typeof a.ts === 'number' &&
                    now - a.ts < Config.EXPERIMENT_ATTRIBUTION_TTL) {
                    fresh[pid] = a;
                }
            }
            return fresh;
        }
        catch (_c) {
            return {};
        }
    };
    /**
     * Persist (or refresh) the variant — and its backing recommendation — a
     * visitor was exposed to for a component.
     */
    Personalize.prototype.persistAssignment = function (personalizationId, variantId, recommendationId) {
        try {
            var assignments = this.readAssignments();
            assignments[String(personalizationId)] = __assign({ variantId: variantId, ts: Date.now() }, (recommendationId != null ? { recommendationId: recommendationId } : {}));
            localStorage.setItem(Config.LS_EXPERIMENT_ASSIGNMENTS, JSON.stringify(assignments));
        }
        catch (_a) {
            // localStorage unavailable: conversions just won't be attributed.
        }
    };
    /**
     * The visitor's most recent (last-touch) experiment exposure within the
     * attribution window, used to attribute a purchase/conversion to a variant.
     * Returns the personalization id, the assigned variant and (if any) the
     * backing recommendation, or undefined when there is no fresh exposure.
     */
    Personalize.prototype.lastAssignment = function () {
        var assignments = this.readAssignments();
        var best;
        for (var _i = 0, _a = Object.entries(assignments); _i < _a.length; _i++) {
            var _b = _a[_i], pid = _b[0], a = _b[1];
            if (!best || a.ts > best.ts) {
                best = __assign({ personalizationId: Number(pid), variantId: a.variantId, ts: a.ts }, (a.recommendationId != null ? { recommendationId: a.recommendationId } : {}));
            }
        }
        if (!best)
            return undefined;
        var ts = best.ts, rest = __rest(best, ["ts"]);
        return rest;
    };
    // Re-apply after the SPA mutates the DOM, so targets that render late still
    // get their component. Self-disarms after a short window to avoid a permanent
    // observer; navigation re-arms it.
    Personalize.prototype.armObserver = function () {
        var _this = this;
        var _a;
        if (typeof MutationObserver === 'undefined')
            return;
        // The tracking snippet can execute before <body> is parsed (e.g. a
        // synchronous <script> in <head>), in which case document.body is still
        // null and MutationObserver.observe() would throw. Defer arming until the
        // DOM is ready instead of assuming the target already exists.
        if (!document.body) {
            document.addEventListener('DOMContentLoaded', function () { return _this.armObserver(); }, {
                once: true,
            });
            return;
        }
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.disconnect();
        window.clearTimeout(this.observerTimer);
        this.observer = new MutationObserver(function () {
            var _a, _b;
            // render() mutates the DOM (cleanup() removes our previous nodes and
            // applyRule() injects new ones). If the observer stayed connected it would
            // observe its own writes and re-fire forever, pinning the tab. Pause
            // observation around the synchronous render so only third-party / SPA
            // mutations re-trigger us.
            (_a = _this.observer) === null || _a === void 0 ? void 0 : _a.disconnect();
            _this.render();
            if (document.body) {
                (_b = _this.observer) === null || _b === void 0 ? void 0 : _b.observe(document.body, { childList: true, subtree: true });
            }
        });
        this.observer.observe(document.body, { childList: true, subtree: true });
        this.observerTimer = window.setTimeout(function () {
            var _a;
            (_a = _this.observer) === null || _a === void 0 ? void 0 : _a.disconnect();
        }, 3000);
    };
    // Intercept SPA navigations (pushState/replaceState/popstate) and re-render.
    // Only a real change of address re-fetches; any other history call (the
    // tracker's own replaceState removing bid/pid, a router re-stating the url,
    // a hash change) just re-renders the components already fetched.
    Personalize.prototype.wireNavigation = function () {
        var _this = this;
        var onNavigate = function () {
            if (_this.navigationKey() !== _this.lastFetchKey) {
                void _this.refresh();
            }
            else {
                _this.render();
            }
            _this.armObserver();
        };
        var history = window.history;
        var wrap = function (method) {
            var original = history[method];
            history[method] = function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                var result = original.apply(this, args);
                window.dispatchEvent(new Event('cdpf:locationchange'));
                return result;
            };
        };
        wrap('pushState');
        wrap('replaceState');
        window.addEventListener('popstate', onNavigate);
        window.addEventListener('cdpf:locationchange', onNavigate);
        this.armObserver();
    };
    return Personalize;
}());
export default Personalize;
